import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Grid,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Container,
  AppBar,
  Toolbar,
  IconButton,
  InputAdornment,
  Tabs,
  Tab,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
} from '@mui/material';
import {
  ArrowBack,
  Save,
  Refresh,
  Lock,
  AccountCircle,
  CloudSync,
  Security,
  QrCode,
  Visibility,
  VisibilityOff,
  ContentCopy,
  CheckCircle,
  Description,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { AuthService, UserData } from '../services/authService';
import { MFAService, MFASecret, MFAStatus } from '../services/mfaService';
import QRCodeDisplay from '../components/QRCodeDisplay';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const SettingsPage: React.FC = () => {
  const { currentUser } = useAuth();
  const [userData, setUserData] = useState<UserData>({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [tabValue, setTabValue] = useState(0);
  
  // Account Settings State
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');

  // MFA State
  const [mfaStatus, setMfaStatus] = useState<MFAStatus>({ enabled: false, backup_codes_count: 0 });
  const [mfaSecret, setMfaSecret] = useState<MFASecret | null>(null);
  const [mfaCode, setMfaCode] = useState('');
  const [showMfaSetup, setShowMfaSetup] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [copiedSecret, setCopiedSecret] = useState(false);
  
  // MFA Disable Dialog State
  const [showDisableDialog, setShowDisableDialog] = useState(false);
  const [disableMfaCode, setDisableMfaCode] = useState('');
  
  // MFA Backup Codes Dialog State
  const [showBackupCodesDialog, setShowBackupCodesDialog] = useState(false);
  const [backupCodesMfaCode, setBackupCodesMfaCode] = useState('');

  // Confluence State
  const [showConfluenceToken, setShowConfluenceToken] = useState(false);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    setMessage('');
  };

  useEffect(() => {
    if (currentUser) {
      loadUserData();
      loadMFAStatus();
    }
  }, [currentUser]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadUserData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const data = await AuthService.getCurrentUserData();
      if (data) {
        setUserData(data);
        console.log('User data loaded successfully:', data);
      } else {
        console.log('No user data found, initializing...');
        // Initialize with empty data if none exists
        setUserData({});
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setMessage('Error loading configuration data');
      setTimeout(() => setMessage(''), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!currentUser) return;
    
    setSaving(true);
    try {
      // Validate data before saving
      if (!AuthService.validateUserData(userData)) {
        throw new Error('Invalid user data format');
      }
      
      await AuthService.saveCurrentUserData(userData);
      setMessage('Configuration saved successfully!');
      console.log('User data saved successfully:', userData);
      setTimeout(() => setMessage(''), 3000);
    } catch (error: any) {
      console.error('Error saving user data:', error);
      setMessage(`Error saving configuration: ${error.message}`);
      setTimeout(() => setMessage(''), 5000);
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: keyof UserData, value: string) => {
    setUserData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const handleEmailVerification = async () => {
    setLoading(true);
    setMessage('');

    try {
      await AuthService.sendEmailVerification();
      setMessage('Verification email sent! Check your inbox.');
    } catch (error: any) {
      setMessage('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  // MFA Functions
  const loadMFAStatus = async () => {
    if (!currentUser) return;
    
    try {
      const status = await MFAService.getMFAStatus(currentUser.uid);
      setMfaStatus(status);
    } catch (error) {
      console.error('Error loading MFA status:', error);
    }
  };

  const handleEnableMFA = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      const secret = await MFAService.enableMFA(currentUser.uid, currentUser.email!);
      setMfaSecret(secret);
      setShowMfaSetup(true);
      setMessage('MFA setup initiated. Please scan the QR code with your authenticator app.');
    } catch (error: any) {
      setMessage(`Error enabling MFA: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyMFA = async () => {
    if (!currentUser || !mfaCode) return;
    
    setLoading(true);
    try {
      const isValid = await MFAService.verifyTOTPCode(currentUser.uid, mfaCode);
      if (isValid) {
        setMessage('MFA verified successfully! MFA is now enabled.');
        setShowMfaSetup(false);
        setMfaCode('');
        await loadMFAStatus();
      } else {
        setMessage('Invalid verification code. Please try again.');
      }
    } catch (error: any) {
      setMessage(`Error verifying MFA: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDisableMFA = () => {
    setShowDisableDialog(true);
    setDisableMfaCode('');
  };

  const handleConfirmDisableMFA = async () => {
    if (!currentUser || !disableMfaCode) return;
    
    setLoading(true);
    try {
      await MFAService.disableMFA(currentUser.uid, disableMfaCode);
      setMessage('MFA disabled successfully.');
      await loadMFAStatus();
      setShowDisableDialog(false);
      setDisableMfaCode('');
    } catch (error: any) {
      setMessage(`Error disabling MFA: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBackupCodes = () => {
    setShowBackupCodesDialog(true);
    setBackupCodesMfaCode('');
  };

  const handleConfirmGenerateBackupCodes = async () => {
    if (!currentUser || !backupCodesMfaCode) return;
    
    setLoading(true);
    try {
      const newCodes = await MFAService.generateNewBackupCodes(currentUser.uid, backupCodesMfaCode);
      setMfaSecret(prev => prev ? { ...prev, backup_codes: newCodes } : null);
      setShowBackupCodesDialog(false);
      setShowBackupCodes(true);
      setMessage('New backup codes generated successfully.');
      setBackupCodesMfaCode('');
    } catch (error: any) {
      setMessage(`Error generating backup codes: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedSecret(true);
      setTimeout(() => setCopiedSecret(false), 2000);
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
    }
  };


  const handlePasswordChange = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setMessage('');

    if (newPassword !== confirmNewPassword) {
      setMessage('Error: New passwords do not match');
      setLoading(false);
      return;
    }

    if (!currentPassword) {
      setMessage('Error: Current password is required');
      setLoading(false);
      return;
    }

    try {
      // Re-authenticate user with current password before updating
      if (currentUser && currentUser.email) {
        await AuthService.signIn(currentUser.email, currentPassword);
      }
      
      await AuthService.updatePassword(newPassword);
      setMessage('Password updated successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (error: any) {
      setMessage('Error: ' + error.message);
    } finally {
      setLoading(false);
    }
  };



  const handleBack = () => {
    window.history.back();
  };


  if (!currentUser) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa' }}>
      <AppBar 
        position="fixed" 
        sx={{ 
          zIndex: (theme) => theme.zIndex.drawer + 1, 
          backgroundColor: '#ffffff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          borderBottom: '1px solid #e0e0e0'
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important', px: 3 }}>
          <IconButton
            edge="start"
            color="inherit"
            onClick={handleBack}
            sx={{ mr: 2, color: '#1976d2' }}
          >
            <ArrowBack />
          </IconButton>
          <Box sx={{ display: 'flex', alignItems: 'center', mr: 4 }}>
            <img
              src="https://rctech.org/wp-content/uploads/2024/09/RCTECH-Logo-Empowering-Decisions-Blue.png"
              alt="RCTECH Logo"
              style={{ height: 32, marginRight: 12, cursor: 'pointer' }}
            />
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 600, 
                color: '#1976d2',
                fontSize: '18px',
                cursor: 'pointer'
              }}
            >
              Settings
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Box sx={{ pt: 8 }}>
        <Container maxWidth="lg" sx={{ py: 4 }}>
        {/* Welcome Section */}
        <Paper elevation={2} sx={{ p: 3, mb: 4, bgcolor: '#e3f2fd' }}>
          <Typography variant="h4" component="h1" gutterBottom color="primary">
            Settings & Configuration
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Manage your account settings, credentials, and security preferences.
          </Typography>
        </Paper>

        {/* Tabs Navigation */}
        <Paper elevation={2} sx={{ mb: 3 }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            variant="fullWidth"
            sx={{ borderBottom: 1, borderColor: 'divider' }}
          >
            <Tab 
              icon={<CloudSync />} 
              label="Configuration" 
              iconPosition="start"
              sx={{ textTransform: 'none', py: 2 }}
            />
            <Tab 
              icon={<AccountCircle />} 
              label="Account Settings" 
              iconPosition="start"
              sx={{ textTransform: 'none', py: 2 }}
            />
            <Tab 
              icon={<Security />} 
              label="Security" 
              iconPosition="start"
              sx={{ textTransform: 'none', py: 2 }}
            />
            <Tab 
              icon={<Description />} 
              label="Confluence" 
              iconPosition="start"
              sx={{ textTransform: 'none', py: 2 }}
            />
          </Tabs>
        </Paper>

        {/* Tab Content */}
        <TabPanel value={tabValue} index={0}>
          {/* Configuration Settings */}
          <Card elevation={2}>
            <CardHeader
              title="Configuration Settings"
              subheader="Manage your Informatica and GitHub credentials"
              avatar={<CloudSync sx={{ color: '#1976d2' }} />}
              action={
                <Box>
                  <Button
                    variant="outlined"
                    startIcon={<Refresh />}
                    onClick={loadUserData}
                    disabled={loading}
                    sx={{ mr: 2 }}
                  >
                    Refresh
                  </Button>
                  <Button
                    variant="contained"
                    startIcon={<Save />}
                    onClick={handleSave}
                    disabled={saving}
                  >
                    {saving ? <CircularProgress size={20} /> : 'Save'}
                  </Button>
                </Box>
              }
            />
            <CardContent>

              {message && (
                <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 3 }}>
                  {message}
                </Alert>
              )}

              {/* Data Status Indicator */}
              {userData.updatedAt && (
                <Box sx={{ mb: 3, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Last Updated:</strong> {new Date(userData.updatedAt).toLocaleString()}
                    {userData.lastLoginAt && (
                      <span style={{ marginLeft: '16px' }}>
                        <strong>Last Login:</strong> {new Date(userData.lastLoginAt).toLocaleString()}
                      </span>
                    )}
                  </Typography>
                </Box>
              )}

              <Grid container spacing={3}>
            {/* Informatica Credentials */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary">
                Informatica Credentials
              </Typography>
              
              {[1, 2, 3].map((num) => (
                <Box key={num} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Informatica Instance {num}
                  </Typography>
                  <TextField
                    fullWidth
                    label={`Username ${num}`}
                    value={userData[`infaUsername${num}` as keyof UserData] || ''}
                    onChange={(e) => handleInputChange(`infaUsername${num}` as keyof UserData, e.target.value)}
                    margin="normal"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label={`Password ${num}`}
                    type="password"
                    value={userData[`infaPassword${num}` as keyof UserData] || ''}
                    onChange={(e) => handleInputChange(`infaPassword${num}` as keyof UserData, e.target.value)}
                    margin="normal"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label={`URL ${num}`}
                    value={userData[`infaUrl${num}` as keyof UserData] || ''}
                    onChange={(e) => handleInputChange(`infaUrl${num}` as keyof UserData, e.target.value)}
                    margin="normal"
                    size="small"
                  />
                </Box>
              ))}
            </Grid>

            {/* GitHub Credentials */}
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom color="primary">
                GitHub Credentials
              </Typography>
              
              <TextField
                fullWidth
                label="GitHub Username"
                value={userData.gitUsername || ''}
                onChange={(e) => handleInputChange('gitUsername', e.target.value)}
                margin="normal"
              />
              <TextField
                fullWidth
                label="GitHub Personal Access Token"
                type="password"
                value={userData.gitPat || ''}
                onChange={(e) => handleInputChange('gitPat', e.target.value)}
                margin="normal"
              />

              <Typography variant="h6" gutterBottom color="primary" sx={{ mt: 3 }}>
                GitHub Repositories
              </Typography>
              
              {[1, 2, 3, 4].map((num) => (
                <Box key={num} sx={{ mb: 2, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Repository {num}
                  </Typography>
                  <TextField
                    fullWidth
                    label={`Repository ${num}`}
                    value={userData[`gitRepo${num}` as keyof UserData] || ''}
                    onChange={(e) => handleInputChange(`gitRepo${num}` as keyof UserData, e.target.value)}
                    margin="normal"
                    size="small"
                  />
                  <TextField
                    fullWidth
                    label={`Branch ${num}`}
                    value={userData[`gitRepo${num}Branch` as keyof UserData] || ''}
                    onChange={(e) => handleInputChange(`gitRepo${num}Branch` as keyof UserData, e.target.value)}
                    margin="normal"
                    size="small"
                  />
                </Box>
              ))}
            </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>


        <TabPanel value={tabValue} index={1}>
          {/* Account Settings */}
          <Card elevation={2}>
            <CardHeader
              title="Account Settings"
              subheader="Manage your password and security preferences"
              avatar={<AccountCircle sx={{ color: '#1976d2' }} />}
            />
            <CardContent>
              {message && (
                <Alert severity={message.includes('Error') ? 'error' : 'success'} sx={{ mb: 3 }}>
                  {message}
                </Alert>
              )}

              <Grid container spacing={3}>
                {/* Email Verification Status */}
                <Grid item xs={12}>
                  <Box sx={{ p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                    <Typography variant="h6" gutterBottom>
                      Email Verification Status
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Chip 
                        label={currentUser?.emailVerified ? 'Verified' : 'Not Verified'} 
                        color={currentUser?.emailVerified ? 'success' : 'warning'}
                        variant="outlined"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {currentUser?.emailVerified 
                          ? 'Your email address has been verified.' 
                          : 'Please verify your email address to secure your account.'
                        }
                      </Typography>
                    </Box>
                  </Box>
                </Grid>

                {/* Change Password */}
                <Grid item xs={12}>
                  <Box sx={{ maxWidth: 500 }}>
                    <Typography variant="h6" gutterBottom color="primary">
                      Change Password
                    </Typography>
                    <form onSubmit={handlePasswordChange}>
                      <TextField
                        fullWidth
                        label="Current Password"
                        type="password"
                        value={currentPassword}
                        onChange={(e) => setCurrentPassword(e.target.value)}
                        required
                        margin="normal"
                        helperText="Enter your current password to verify your identity"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        label="New Password"
                        type="password"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        required
                        margin="normal"
                        helperText="Minimum 6 characters"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        fullWidth
                        label="Confirm New Password"
                        type="password"
                        value={confirmNewPassword}
                        onChange={(e) => setConfirmNewPassword(e.target.value)}
                        required
                        margin="normal"
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <Lock color="primary" />
                            </InputAdornment>
                          ),
                        }}
                      />
                      <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                        fullWidth
                        sx={{ mt: 3 }}
                      >
                        {loading ? <CircularProgress size={24} /> : 'Update Password'}
                      </Button>
                    </form>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Security Settings */}
          <Card elevation={2}>
            <CardHeader
              title="Multi-Factor Authentication (MFA)"
              subheader="Add an extra layer of security to your account"
              avatar={<Security color="primary" />}
            />
            <CardContent>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6" sx={{ flexGrow: 1 }}>
                      Authentication Status
                    </Typography>
                    <Chip
                      icon={mfaStatus.enabled ? <CheckCircle /> : <Lock />}
                      label={mfaStatus.enabled ? 'Enabled' : 'Disabled'}
                      color={mfaStatus.enabled ? 'success' : 'default'}
                      variant="outlined"
                    />
                  </Box>
                  
                  {!mfaStatus.enabled ? (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Enable MFA to protect your account with Google Authenticator or similar apps.
                      </Typography>
                      <Button
                        variant="contained"
                        startIcon={<QrCode />}
                        onClick={handleEnableMFA}
                        disabled={loading}
                        sx={{ mb: 2 }}
                      >
                        {loading ? <CircularProgress size={20} /> : 'Enable MFA'}
                      </Button>
                    </Box>
                  ) : (
                    <Box>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        MFA is currently enabled. You can disable it or generate new backup codes.
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <Button
                          variant="outlined"
                          onClick={handleGenerateBackupCodes}
                          disabled={loading}
                        >
                          {loading ? <CircularProgress size={20} /> : 'Generate Backup Codes'}
                        </Button>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={handleDisableMFA}
                          disabled={loading}
                        >
                          {loading ? <CircularProgress size={20} /> : 'Disable MFA'}
                        </Button>
                      </Box>
                    </Box>
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Confluence Settings */}
          <Card elevation={2}>
            <CardHeader
              title="Confluence Credentials"
              subheader="Manage your Confluence API credentials and default spaces"
              avatar={<Description color="primary" />}
            />
            <CardContent>
              <Grid container spacing={3}>
                {/* Confluence Username/Email */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Confluence Email ID"
                    value={userData.confluenceUsername || ''}
                    onChange={(e) => handleInputChange('confluenceUsername', e.target.value)}
                    placeholder="user@company.com"
                    helperText="Your Confluence account email address"
                  />
                </Grid>

                {/* Confluence API Token */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Confluence API Token"
                    type={showConfluenceToken ? "text" : "password"}
                    value={userData.confluenceApiToken || ''}
                    onChange={(e) => handleInputChange('confluenceApiToken', e.target.value)}
                    placeholder="ATATT3xFfGF0pddu4vP8FA-eiwpro-lq-wLwghMqT9EYZHgSgoQZySFRIGMyO2j7Mp6eb7hDawFnUAwK7qm3UeJBuTuKvxG4GTZK8TlcVMjqI_dm3K04VDUgnJ0AHPLgIEQeDB7QjqsRUfsbSEXgYvTnHr0gSqzg2o6TQYyLt8N9WNEt_sZQTGs=8CFE8676"
                    helperText="Generate this from Confluence > Settings > API tokens"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowConfluenceToken(!showConfluenceToken)}
                            edge="end"
                          >
                            {showConfluenceToken ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Confluence Base URL */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Confluence Base URL"
                    value={userData.confluenceBaseUrl || ''}
                    onChange={(e) => handleInputChange('confluenceBaseUrl', e.target.value)}
                    placeholder="https://yourcompany.atlassian.net"
                    helperText="Your Confluence instance URL"
                  />
                </Grid>

                {/* Cloud ID ARI */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Cloud ID ARI"
                    value={userData.confluenceCloudIdAri || ''}
                    onChange={(e) => handleInputChange('confluenceCloudIdAri', e.target.value)}
                    placeholder="ari:cloud:confluence::site/b1e6bffa-8fb3-4962-93f3-d68d71275d36"
                    helperText="Confluence Cloud ID ARI identifier"
                  />
                </Grid>

                {/* Default Spaces */}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Default Spaces"
                    value={userData.confluenceDefaultSpaces || ''}
                    onChange={(e) => handleInputChange('confluenceDefaultSpaces', e.target.value)}
                    placeholder="ENG, HR, DEV"
                    helperText="Comma-separated list of default Confluence spaces"
                    multiline
                    rows={2}
                  />
                </Grid>

                {/* Save Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                    <Button
                      variant="outlined"
                      onClick={loadUserData}
                      startIcon={<Refresh />}
                    >
                      Refresh
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSave}
                      startIcon={<Save />}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={20} /> : 'Save Confluence Settings'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </TabPanel>

        {/* MFA Setup Dialog */}
        <Dialog open={showMfaSetup} onClose={() => setShowMfaSetup(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Set Up Multi-Factor Authentication</DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', py: 2 }}>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Scan this QR code with your authenticator app:
              </Typography>
              
              {/* QR Code */}
              {mfaSecret?.qr_code && (
                <QRCodeDisplay 
                  qrCodeDataUrl={mfaSecret.qr_code} 
                  size={200}
                />
              )}
              
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Or enter this secret key manually:
              </Typography>
              
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <TextField
                  value={mfaSecret?.secret || ''}
                  variant="outlined"
                  size="small"
                  sx={{ flexGrow: 1, mr: 1 }}
                  InputProps={{
                    type: showSecret ? 'text' : 'password',
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowSecret(!showSecret)}
                          edge="end"
                        >
                          {showSecret ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                        <IconButton
                          onClick={() => copyToClipboard(mfaSecret?.secret || '')}
                          edge="end"
                        >
                          {copiedSecret ? <CheckCircle color="success" /> : <ContentCopy />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="h6" sx={{ mb: 2 }}>
                Enter verification code:
              </Typography>
              
              <TextField
                fullWidth
                label="6-digit code"
                value={mfaCode}
                onChange={(e) => setMfaCode(e.target.value)}
                placeholder="123456"
                sx={{ mb: 2 }}
                inputProps={{ maxLength: 6 }}
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowMfaSetup(false)}>Cancel</Button>
            <Button
              onClick={handleVerifyMFA}
              variant="contained"
              disabled={loading || mfaCode.length !== 6}
            >
              {loading ? <CircularProgress size={20} /> : 'Verify & Enable'}
            </Button>
          </DialogActions>
        </Dialog>

            {/* Backup Codes Dialog */}
            <Dialog open={showBackupCodes} onClose={() => setShowBackupCodes(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Backup Codes</DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Save these backup codes in a safe place. Each code can only be used once.
                </Alert>
                
                <List>
                  {mfaSecret?.backup_codes.map((code, index) => (
                    <ListItem key={index} divider>
                      <ListItemText primary={code} />
                      <ListItemSecondaryAction>
                        <IconButton
                          onClick={() => copyToClipboard(code)}
                          edge="end"
                        >
                          <ContentCopy />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </ListItem>
                  ))}
                </List>
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowBackupCodes(false)}>Close</Button>
              </DialogActions>
            </Dialog>

            {/* Disable MFA Dialog */}
            <Dialog open={showDisableDialog} onClose={() => setShowDisableDialog(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Disable Multi-Factor Authentication</DialogTitle>
              <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  To disable MFA, please enter your current 6-digit authentication code from your authenticator app.
                </Alert>
                
                <TextField
                  fullWidth
                  label="6-digit authentication code"
                  value={disableMfaCode}
                  onChange={(e) => setDisableMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 6 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowDisableDialog(false)}>Cancel</Button>
                <Button
                  onClick={handleConfirmDisableMFA}
                  variant="contained"
                  color="error"
                  disabled={loading || disableMfaCode.length !== 6}
                >
                  {loading ? <CircularProgress size={20} /> : 'Disable MFA'}
                </Button>
              </DialogActions>
            </Dialog>

            {/* Generate Backup Codes Dialog */}
            <Dialog open={showBackupCodesDialog} onClose={() => setShowBackupCodesDialog(false)} maxWidth="sm" fullWidth>
              <DialogTitle>Generate New Backup Codes</DialogTitle>
              <DialogContent>
                <Alert severity="info" sx={{ mb: 2 }}>
                  To generate new backup codes, please enter your current 6-digit authentication code from your authenticator app.
                </Alert>
                
                <TextField
                  fullWidth
                  label="6-digit authentication code"
                  value={backupCodesMfaCode}
                  onChange={(e) => setBackupCodesMfaCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  placeholder="123456"
                  sx={{ mb: 2 }}
                  inputProps={{ maxLength: 6 }}
                />
              </DialogContent>
              <DialogActions>
                <Button onClick={() => setShowBackupCodesDialog(false)}>Cancel</Button>
                <Button
                  onClick={handleConfirmGenerateBackupCodes}
                  variant="contained"
                  disabled={loading || backupCodesMfaCode.length !== 6}
                >
                  {loading ? <CircularProgress size={20} /> : 'Generate Codes'}
                </Button>
              </DialogActions>
            </Dialog>

        </Container>
      </Box>
    </Box>
  );
};

export default SettingsPage;
